import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { THERMOBOX_FORM_NAME, REQUEST_ERROR_MESSAGE, FIELDS_FOR_VALIDATE } from './constants';
import {
	createThermoboxRequest,
	deleteThermoboxRequest,
	getThermoboxInfoRequest,
	updateThermoboxRequest,
} from '../../api/requests';
import { getPartnersRequest } from '../../api/partners-requests';
import {
	GET_THERMOBOX_INFO,
	setThermoboxInfoAction,
	setIsThermoboxInfoLoadingAction,
	CREATE_THERMOBOX,
	setIsThermoboxCreatingAction,
	UPDATE_THERMOBOX,
	setIsThermoboxUpdatingAction,
	DELETE_THERMOBOX,
	setIsThermoboxDeletingAction,
	GET_PARTNERS,
	setPartnersAction,
	setIsPartnersLoadingAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { getThermoboxesAction } from '../thermoboxes/actions';
import { transformToValidationErrors } from './utils';

export function* getThermoboxInfoSaga({ payload: id }) {
	try {
		yield put(setIsThermoboxInfoLoadingAction(true));

		const { data, message, toast } = yield call(getThermoboxInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setThermoboxInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxInfoLoadingAction(false));
	}
}

export function* createThermoboxSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsThermoboxCreatingAction(true));

		const dataForSave = yield select(getDataForSaveSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createThermoboxRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			redirect();

			yield put(getThermoboxesAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(THERMOBOX_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(THERMOBOX_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxCreatingAction(false));
	}
}

export function* updateThermoboxSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsThermoboxUpdatingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(updateThermoboxRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setThermoboxInfoAction(data));

			redirect();

			yield put(getThermoboxesAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(THERMOBOX_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(THERMOBOX_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxUpdatingAction(false));
	}
}

export function* deleteThermoboxSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsThermoboxDeletingAction(true));

		const { data, message, toast } = yield call(deleteThermoboxRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();

			yield put(getThermoboxesAction({ searchQuery, noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxDeletingAction(false));
	}
}

export function* getPartnersSaga() {
	try {
		yield put(setIsPartnersLoadingAction(true));

		const { data, message, toast } = yield call(getPartnersRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setPartnersAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsPartnersLoadingAction(false));
	}
}

export default function* icepackModalSaga() {
	yield takeEvery(GET_THERMOBOX_INFO, getThermoboxInfoSaga);
	yield takeEvery(CREATE_THERMOBOX, createThermoboxSaga);
	yield takeEvery(UPDATE_THERMOBOX, updateThermoboxSaga);
	yield takeEvery(DELETE_THERMOBOX, deleteThermoboxSaga);
	yield takeEvery(GET_PARTNERS, getPartnersSaga);
}
